import { useEffect, useState } from 'react';
import type { Ticket } from '@/entities/ticket';
import { apiErrorText, mailApi } from '@/shared/api';
import { ticketsApi } from '@/shared/api/tickets';
import { Button, Callout, Dialog, DialogContent, DialogFooter, DialogTitle, Field, Input, toast } from '@/shared/ui';

interface Props {
  ticketId: string;
  messageId: string | null;
  onClose: () => void;
  onMoved: (messageId: string) => void;
}

export function MoveMessageDialog({ ticketId, messageId, onClose, onMoved }: Props) {
  const [number, setNumber] = useState('');
  const [target, setTarget] = useState<Ticket | null>(null);
  const [lookup, setLookup] = useState<'idle' | 'busy' | 'none'>('idle');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!messageId) return;
    setNumber('');
    setTarget(null);
    setLookup('idle');
  }, [messageId]);

  useEffect(() => {
    const n = Number(number.replace(/\D/g, ''));
    setTarget(null);
    if (!n) {
      setLookup('idle');
      return;
    }
    setLookup('busy');
    const timer = window.setTimeout(() => {
      ticketsApi
        .list({ q: String(n), limit: 10, includeClosed: true })
        .then((res) => {
          const found = res.data.find((t) => t.number === n && t.id !== ticketId) ?? null;
          setTarget(found);
          setLookup(found ? 'idle' : 'none');
        })
        .catch(() => setLookup('none'));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [number, ticketId]);

  const submit = async () => {
    if (!messageId || !target) return;
    setSaving(true);
    try {
      await mailApi.moveMessage(ticketId, messageId, target.id);
      toast.success(`Сообщение перенесено в заявку №${target.number}`);
      onMoved(messageId);
      onClose();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось перенести сообщение'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={messageId != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogTitle>Перенести сообщение в другую заявку</DialogTitle>
        <Field label="Номер заявки">
          <Input
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="Например, 1042"
            inputMode="numeric"
            autoFocus
          />
        </Field>
        {target && (
          <Callout tone="info" title={`№${target.number} · ${target.title}`}>
            Сообщение, его файлы и письмо уедут туда. В обеих заявках останется запись о переносе.
          </Callout>
        )}
        {lookup === 'none' && <Callout tone="muted">Заявки с таким номером не нашли.</Callout>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={!target || saving}>
            Перенести
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
